import React, { useEffect, useState } from 'react'
import { Container, Typography, Grid, Card, Button, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Link, makeStyles } from '@material-ui/core'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useRouter } from 'next/router'
import { useAuth, useFetch } from '../context/useFetch'
import { useBatch, useUpdate } from '../reducer'
import i18n from '../i18n/i18n'

const useStyles = makeStyles(() => ({
  dialog: {
    height: '800px'
  }
}))

export const Interviews = () => {
  const profileType = useSelector((state) => state.profile.user.profile_type)
  const interviews = useSelector((state) => state.interviews)

  if (profileType !== 'Company' || interviews?.length === 0) return null

  return (
    <Container>
      <Typography variant="h2" align="left" style={{ marginTop: '5%' }}>
        {i18n.t('profile.entreprise.interview.title')}
      </Typography>
      <InterviewsOfTheDay />
    </Container>
  )
}

const InterviewsOfTheDay = () => useFetch(
  ({ fetch }) => ({
    interviews: fetch('interviews?today=true')
  }),
  ({ interviews }) => (
    <Grid container justifyContent="center" spacing={2} style={{ maxHeight: '50vh', overflow: 'auto' }}>
      {Object.values(interviews).map((interview) => (
        <Grid item xs={8}>
          <Card
            style={{
              boxShadow: '0px 16px 40px rgba(112, 144, 176, 0.2)',
              padding: '12px'
            }}
          >
            <SingleInterviewCard interview={interview} />
          </Card>
        </Grid>
      ))}
    </Grid>
  )
)

const SingleInterviewCard = ({ interview }) => {
  const candidateName = `${interview.job_application?.candidate.first_name} ${interview.job_application?.candidate.last_name}`
  const candidateTitle = interview.job_application.candidate.title 
  const userName = interview.user.name

  const [openDialog, setOpenDialog] = useState(false)

  const candidateCvHashid = interview.job_application.candidate.documents.find((document) => document.document_type.slug === "cv").hashid

  const auth = useAuth()
  const theme = useTheme()

  const displayDocument = (e) => {
    e.preventDefault()
    e.stopPropagation()

    auth.fetch(`documents/${candidateCvHashid}/download`).then(({ url }) => {
      window.open(url, '_blank')
    })
  }

  const handleOpenDialog = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setOpenDialog(true)
  }

  const interviewInfos = {
    interview,
    application: interview.job_application,
    offerName: interview.job_application.job_offer.label,
    candidateFullName: candidateName
  }

  return (
    <Link href={`/entreprise/profile/application/${interview.job_application.hashid}`} style={{ textDecoration: 'none' }}>
      <Grid container alignItems="center" textAlign="left" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="h3" color="primary">{interview.formatted_time}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="paragraph" color="textSecondary">{i18n.t('profile.entreprise.interview.created_by', { user: userName })}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Button variant="contained" size="small" style={{ background: theme.palette.primary.main, color: 'white', marginRight: '10px' }} onClick={(e) => handleOpenDialog(e)}>{i18n.t('profile.entreprise.interview.edit')}</Button>
          <Button variant="outlined" size="small" color="primary" onClick={(e) => displayDocument(e)}>{i18n.t('profile.entreprise.interview.see_CV')}</Button>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="h3">{candidateName}</Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography>{candidateTitle}</Typography>
        </Grid>
        {openDialog && (
          <InterviewDialog
            open={openDialog}
            setOpen={(value) => setOpenDialog(value)}
            interviewInfos={interviewInfos}
          />
        )}
      </Grid>
    </Link>
  )
}

export const InterviewDialog = ({ open, setOpen, interviewInfos }) => {
  const { interview, application, offerName, candidateFullName } = interviewInfos

  const interviewsList = useSelector((state) => state.interviews) || []

  const auth = useAuth()
  const update = useUpdate()
  const batch = useBatch()
  const router = useRouter()
  const theme = useTheme()

  const [date, setDate] = useState(interview?.date || moment().add(1, 'days').format('YYYY-MM-DD'))
  const [time, setTime] = useState('10:00')

  const classes = useStyles()

  const scheduleInterview = () => {
    const scheduledinterview = {
      date,
      time
    }

    auth.fetch(`profile/applications/${application.id}/interview`, {
      method: 'POST',
      body: scheduledinterview
    }).then(() => {
      if (router.pathname.includes('job-offer')) update(['profileOffer', 'included', 'applications_recaps', application.id, 'scheduled_interview'], true)
      else if (router.pathname.includes('profile/application')) update(['interviews'], { ...scheduledinterview, formatted_time: time })

      else {
        //Update state from interviews list in Dashboard
        const selectedInterviewIndex = interviewsList.findIndex((item) => item === interview)
        if (interview.date !== date && date !== moment().format('YYYY-MM-DD')) {
          const filteredList = interviewsList.filter((item) => item !== interview)
          update(['interviews'], filteredList)
        } else {
          batch(['interviews', selectedInterviewIndex], {
            date,
            formatted_time: time
          })
        }
      }
      setOpen(false)
    }).catch(() => alert('error', i18n.t('alerts.error')))
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className={classes.dialog}>
      <DialogTitle>{i18n.t('profile.entreprise.interview.dialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography style={{ fontSize: '14px' }}>
            {i18n.t('profile.entreprise.interview.dialog.subtitle.1')}
          </Typography>
          <Typography style={{ fontSize: '14px' }}>
            {i18n.t('profile.entreprise.interview.dialog.subtitle.2')}
          </Typography>
        </DialogContentText>
        <Container>
          <Grid container spacing={3} style={{ marginTop: '20px' }}>
            <Grid item xs={12}>
              <Typography style={{ fontWeight: 'bold' }}>{i18n.t('profile.entreprise.interview.dialog.offer_name', { offer: offerName })}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                {candidateFullName}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>{application?.label}</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                type="date"
                value={date}
                style={{
                  marginRight: '20px'
                }}
                InputLabelProps={{ shrink: true, style: { color: 'white' } }}
                label={i18n.t('landing_page.contact_form.select_date')}
                onChange={(e) => setDate(e.target.value)}
              />
              <TextField
                type="time"
                value={time}
                InputLabelProps={{
                  shrink: true,
                  style: {
                    minWidth: '150px',
                    backgroundColor: 'transparent',
                    color: 'white'
                  }
                }}
                inputProps={{
                  style: {
                    paddingLeft: '5px'
                  }
                }}
                label={i18n.t('landing_page.contact_form.select_time')}
                onChange={(e) => setTime(e.target.value)}
              />
            </Grid>
          </Grid>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => setOpen(false)}>
          {i18n.t('generic.cancel')}
        </Button>
        <Button variant="contained" style={{ background: theme.palette.primary.main, color: 'white' }} onClick={() => scheduleInterview()}>
          {i18n.t('generic.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
